import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState, useEffect } from 'react';
import { 
  AlertDialog, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle 
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface DeleteFolderModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  folderNames: string[];
  onConfirm: () => Promise<void>;
}

export function DeleteFolderModal({
  open,
  onOpenChange,
  folderNames,
  onConfirm
}: DeleteFolderModalProps) {
  useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('videos', 'edit');

  useEffect(() => {
    if (open && !canEdit) {
      toast.error(translateInterfaceText("Sem permissão para excluir pastas"));
      onOpenChange(false);
    }
  }, [open, canEdit, onOpenChange]);
  
  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (error) {
      // Toast já foi exibido no handler
    } finally {
      setIsLoading(false);
    }
  };
  
  const isMultiple = folderNames.length > 1;
  
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="w-[92vw] sm:w-[clamp(360px,38vw,440px)] sm:max-w-none p-0 rounded-2xl">
        <AlertDialogHeader className="px-5 py-4">
          <AlertDialogTitle>
            {isMultiple 
              ? translateInterfaceText("Excluir {value0} pastas?", { value0: (folderNames.length) })
              : translateInterfaceText("Excluir pasta?")
            }
          </AlertDialogTitle>
        </AlertDialogHeader>
        
        <div className="px-5 pb-4">
          <AlertDialogDescription>
            {translateInterfaceText("Esta ação remove a pasta e suas subpastas da sua organização. Os vídeos não serão apagados do sistema.")}</AlertDialogDescription>
        </div>
        
        <AlertDialogFooter className="px-5 py-4 border-t">
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
            autoFocus
          >
            {translateInterfaceText("Cancelar")}</Button>
          <Button 
            variant="destructive" 
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {translateInterfaceText("Excluir")}</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
